import Link from "next/link";
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { cn } from "@/lib/utils";
import { Copy, Download, Maximize2, Search } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  FanOutIterationCounts,
  FanOutIterationPager,
} from "@/components/Spotflow/WorkflowRunner/Outputs/FanOutIterationPager";
import { IterationOutcomeRows } from "@/components/Spotflow/WorkflowRunner/Outputs/IterationOutcomeRows";
import {
  ioListItemIndex,
  ioListWindowOffset,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ExpandableNode/ioListWindow";
import type { IterationOutcomes } from "@/generated";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { copyToClipboard } from "@/utils/generic";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { DataTypeBadge } from "@/icons/DataTypeIcon";
import {
  NoxusJsonView,
  tryParseJsonObject,
} from "@/components/UI/NoxusJsonView";
import { useAtomValue } from "jotai";
import { useSpotUser } from "@/utils/spotUser";
import { useNodeIo } from "@/components/Spotflow/WorkflowRunner/Outputs/useNodeIo";
import {
  IoFileRow,
  coerceIoFileLike,
  coerceIoTextContainer,
  getIoFileHref,
  isIoFileType,
  normalizeIoContainerType,
  normalizePreviewIoContainer,
  type IoTextContainer,
  type NormalizedIoContainer,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ioFileDisplay";
import { CompactListPreview } from "@/components/Spotflow/WorkflowRunner/Outputs/ioListPreview";
import { SpotMarkdown } from "@/components/Spotflow/SpotMarkdown/SpotMarkdown";
import {
  SkippedBadge,
  isSkippedValue,
} from "@/components/Spotflow/WorkflowRunner/Outputs/skippedDisplay";
import { IoEmptyValue } from "@/components/Spotflow/WorkflowRunner/Outputs/ioEmptyValue";
import {
  IterationWidthErrorView,
  iterationWidths,
} from "@/components/Spotflow/WorkflowRunner/Outputs/iterationWidthError";
import { V2OutputRow } from "@/components/Spotflow/WorkflowRunner/Outputs/V2OutputRow";
import {
  ITERATION_OUTCOMES_KEY,
  firstFailedIteration,
  iterationPage,
  readIterationOutcomes,
  v2FanOutPageCount,
} from "@/components/Spotflow/WorkflowRunner/Outputs/v2FanOut";
import {
  PlaybookResultsViewer,
  type ExecutionResults,
} from "@/components/BrowserAutomation/PlaybookResultsViewer";
import {
  nodeDataByIdAtom,
  nodeRegistryAtom,
  nodeRunProgressAtom,
  nodeIoFetchPathAtom,
  previewRunIdAtom,
} from "@/store/editor-v2";
import { SOURCE_ONLY_TYPES } from "@/components/SpotflowV2/constants";
import { dynamicHandlesFromConfig } from "@/components/SpotflowV2/Sidebar/smartRouteBindings";
import { pathVarLabel } from "@/components/SpotflowV2/Sidebar/pathValues";
import { conditionGateRows } from "./conditionGateRows";
import { useEffectiveRegEntry } from "./hooks/useEffectiveRegEntry";
import { useNodeInputs } from "./hooks/useNodeInputs";
import { useNodeOutputs } from "./hooks/useNodeOutputs";
import { useNodeBindings } from "./hooks/useNodeBindings";
import type { NodeV2Data } from "@/types/spotflow-v2";

export type IoContainer = NormalizedIoContainer;

// Browser Playbook ships its step-by-step results (screenshots included) as
// this extra IO key — not a declared output, so it never reaches the variable
// picker. Rendered as the playbook results viewer instead of a raw value.
const EXECUTION_PROGRESS_KEY = "execution_progress";

function coerceExecutionProgress(
  container: IoContainer | undefined
): ExecutionResults | null {
  if (!container) return null;
  let raw = container.value;
  const textContainer = coerceIoTextContainer(raw);
  if (textContainer) raw = textContainer.text;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    Array.isArray((raw as { steps?: unknown }).steps)
  ) {
    return raw as ExecutionResults;
  }
  return null;
}

const MAX_PREVIEW_OUTPUTS = 5;
const EMPTY_NODE_DATA: NodeV2Data = {
  nodeType: "",
  name: "",
  nodeConfig: {},
  variableBindings: {},
  outputHandles: [],
  inputHandles: [],
  pinnedFields: [],
  iterate: false,
  iterateOverrides: {},
  title: "",
  color: "",
  deletable: true,
};

// Gate: outside run preview the panel never renders, so none of the data
// hooks below (bindings, outputs, IO fetch) should mount for every card.
export const PreviewIoPanel = memo(function PreviewIoPanel({
  nodeId,
}: {
  nodeId: string;
}) {
  const previewRunId = useAtomValue(previewRunIdAtom);
  if (!previewRunId) return null;
  return <PreviewIoPanelData nodeId={nodeId} />;
});

const PreviewIoPanelData = memo(function PreviewIoPanelData({
  nodeId,
}: {
  nodeId: string;
}) {
  const previewRunId = useAtomValue(previewRunIdAtom);
  const nodeProgress = useAtomValue(nodeRunProgressAtom(nodeId));
  const nodeRunStatus = nodeProgress?.status;
  const { spotGroup } = useSpotUser();
  const data = useAtomValue(nodeDataByIdAtom(nodeId));
  const nodeRegistry = useAtomValue(nodeRegistryAtom);
  const registryEntry = nodeRegistry.find((r) => r.type === data?.nodeType);
  const regEntry = useEffectiveRegEntry(nodeId, registryEntry);
  const gathersListNode = !!(regEntry as { gathers_list?: boolean } | undefined)
    ?.gathers_list;
  const isSourceOnly = SOURCE_ONLY_TYPES.has(data?.nodeType ?? "");
  const pickerVisibleInputs = useNodeInputs(regEntry, isSourceOnly);
  const nodeData = data ?? EMPTY_NODE_DATA;
  const { receivesListInput } = useNodeBindings(nodeData, pickerVisibleInputs);
  const { effectiveOutputs } = useNodeOutputs(
    nodeData,
    regEntry,
    gathersListNode,
    receivesListInput
  );

  const outputLabelByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const output of effectiveOutputs) {
      map[output.name] = output.label;
    }
    return map;
  }, [effectiveOutputs]);

  const outputIsListByName = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const output of effectiveOutputs) {
      map[output.name] = output.isList;
    }
    return map;
  }, [effectiveOutputs]);

  const outputDataTypeByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const output of effectiveOutputs) {
      map[output.name] = output.dataType;
    }
    return map;
  }, [effectiveOutputs]);

  // The node's full static output schema, keyed by name. A dynamic-config node
  // (e.g. Text Operations) only declares the outputs for its current operation,
  // but the run still emits every declared output (the inactive ones Skipped).
  // Falling back to the static type keeps their badge correct (e.g. a `split`
  // run's `boolean` shows "Boolean" not the value-inferred "Object").
  const staticOutputDataTypeByName = useMemo(() => {
    const map: Record<string, string> = {};
    const outs =
      (
        registryEntry as
          | { outputs?: Array<{ name?: string; data_type?: string }> }
          | undefined
      )?.outputs ?? [];
    for (const o of outs) {
      if (o.name && o.data_type) map[o.name] = o.data_type;
    }
    return map;
  }, [registryEntry]);

  // Routing nodes (Switch / Smart Route) emit branch outputs keyed by stable id
  // (a UUID), so the connector-label map above won't have them — fall back to
  // the config-derived handle labels (branch/route `name`, plus `default`).
  const dynamicHandleLabelByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const handle of dynamicHandlesFromConfig(
      data?.nodeType,
      data?.nodeConfig
    )) {
      map[handle.id] = handle.label;
    }
    return map;
  }, [data?.nodeType, data?.nodeConfig]);

  const resolveOutputLabel = useCallback(
    (key: string) => {
      if (key === "_error") return "Error";
      return (
        outputLabelByName[key] ??
        dynamicHandleLabelByName[key] ??
        pathVarLabel(data?.nodeType, registryEntry, data?.nodeConfig, key) ??
        key
      );
    },
    [
      outputLabelByName,
      dynamicHandleLabelByName,
      data?.nodeType,
      data?.nodeConfig,
      registryEntry,
    ]
  );

  const resolveOutputIsList = useCallback(
    (key: string) => outputIsListByName[key] ?? false,
    [outputIsListByName]
  );

  // The run IO's own `type` is what the run drawer shows: the backend already
  // let the node's declared type win over inference when it stored the value,
  // so trusting it first keeps every results surface on one label. The
  // editor-side declared type only fills in when the container has none.
  const resolveOutputDataType = useCallback(
    (key: string, container: IoContainer) => {
      if (container.type && !container.type.startsWith("_")) {
        return container.type;
      }
      const declared =
        outputDataTypeByName[key] ?? staticOutputDataTypeByName[key];
      if (declared) return declared;
      const raw = container.value;
      if (Array.isArray(raw)) {
        return listKindToDataType(inferListKind(raw, container.type));
      }
      const normalized = normalizeIoContainerType(container.type, raw);
      if (normalized === "File") return "File";
      if (typeof raw === "number") return "number";
      if (typeof raw === "boolean") return "bool";
      return "str";
    },
    [outputDataTypeByName, staticOutputDataTypeByName]
  );

  const { effectiveNodeId, it: iteration } = useAtomValue(
    nodeIoFetchPathAtom(nodeId)
  );
  // Fan-out page shown in the Run output dialog. Lives here because a page
  // past the endpoint's list cap needs its window fetched. Offset 0 keeps
  // the query key identical to the run drawer's, so they share one entry.
  const [fanOutPage, setFanOutPage] = useState(1);
  const listOffset = ioListWindowOffset(fanOutPage);
  const { data: ioData } = useNodeIo(
    {
      path: {
        group_id: spotGroup.id,
        run_id: previewRunId!,
        node_id: effectiveNodeId,
        it: iteration,
      },
      query: {
        ...(listOffset > 0 ? { list_offset: listOffset } : {}),
      },
    },
    nodeProgress,
    {
      enabled: !!previewRunId && !!nodeRunStatus && nodeRunStatus !== "running",
      retry: 5,
      retryDelay: 1000,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // A node paused for human feedback hasn't produced outputs yet — the empty
  // "No outputs" pane is just noise while it waits.
  if (nodeRunStatus === "awaiting_human_feedback") return null;
  if (!previewRunId || !ioData) return null;

  const rawOutputs =
    (ioData as { outputs?: Record<string, unknown> }).outputs ?? {};

  return (
    <div
      className="absolute left-0 right-0 nodrag nowheel"
      style={{ top: "calc(100% + 6px)", zIndex: 5 }}
    >
      <PreviewIoPanelInner
        nodeId={nodeId}
        status={nodeRunStatus}
        inputs={{}}
        outputs={Object.fromEntries(
          conditionGateRows(
            Object.entries(rawOutputs)
              .filter(([key]) => key !== ITERATION_OUTCOMES_KEY)
              .map(([key, entry]): [string, IoContainer] => [
                key,
                normalizePreviewIoContainer(
                  entry,
                  outputIsListByName[key] ?? false
                ),
              ])
          )
        )}
        resolveOutputLabel={resolveOutputLabel}
        resolveOutputDataType={resolveOutputDataType}
        resolveOutputIsList={resolveOutputIsList}
        totalIterations={nodeProgress?.total_it ?? 1}
        iterationOutcomes={readIterationOutcomes(
          rawOutputs[ITERATION_OUTCOMES_KEY]
        )}
        iteration={fanOutPage}
        setIteration={setFanOutPage}
      />
    </div>
  );
});

function PreviewIoPanelInner({
  nodeId,
  status,
  inputs,
  outputs,
  resolveOutputLabel,
  resolveOutputDataType,
  resolveOutputIsList,
  totalIterations,
  iterationOutcomes,
  iteration,
  setIteration,
}: {
  nodeId: string;
  status: string | undefined;
  inputs: Record<string, IoContainer>;
  outputs: Record<string, IoContainer>;
  resolveOutputLabel: (key: string) => string;
  resolveOutputDataType: (key: string, container: IoContainer) => string;
  resolveOutputIsList: (key: string) => boolean;
  totalIterations: number;
  iterationOutcomes: IterationOutcomes | null;
  iteration: number;
  setIteration: Dispatch<SetStateAction<number>>;
}) {
  const [open, setOpen] = useState(false);
  const inputEntries = Object.entries(inputs);
  const executionProgress = coerceExecutionProgress(
    outputs[EXECUTION_PROGRESS_KEY]
  );
  const outputEntries = Object.entries(outputs).filter(
    ([key]) => !(executionProgress && key === EXECUTION_PROGRESS_KEY)
  );
  const pageCount = v2FanOutPageCount(
    totalIterations,
    outputEntries.map(([, container]) => container.value),
    iterationOutcomes
  );
  const paged = pageCount > 1;
  const currentPage = Math.min(iteration, pageCount);
  const outcomePage = iterationOutcomes
    ? iterationPage(iterationOutcomes, currentPage)
    : null;
  const visibleOutputEntries = outputEntries.slice(0, MAX_PREVIEW_OUTPUTS);
  const hiddenOutputCount = outputEntries.length - visibleOutputEntries.length;

  const accentClass =
    status === "errored"
      ? "border-red-200/70 dark:border-red-800/40"
      : "border-border/60";

  return (
    <>
      <div
        data-testid="node-run-output-card"
        className={cn(
          "mt-1.5 w-full rounded-lg border bg-card shadow-sm overflow-hidden nodrag nowheel relative cursor-pointer hover:border-border",
          accentClass
        )}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <div className="absolute top-2 right-2 opacity-30 hover:opacity-80 transition-opacity nodrag z-10">
          <Maximize2 size={10} />
        </div>

        {inputEntries.length > 0 && (
          <div className="px-3 py-2.5 border-b border-border/40">
            <p className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/40 mb-2">
              Inputs
            </p>
            <div className="space-y-2.5">
              {inputEntries.map(([key, container]) => (
                <IoEntry
                  key={key}
                  nodeId={nodeId}
                  label={key}
                  container={container}
                  dataType={resolveOutputDataType(key, container)}
                />
              ))}
            </div>
          </div>
        )}
        <div className="px-3 py-2.5">
          {executionProgress && (
            <div className={outputEntries.length > 0 ? "mb-2.5" : undefined}>
              <IoOutputLabel label="Playbook steps" />
              <span className="inline-block rounded-md border border-border/50 bg-muted/20 px-1.5 py-0.5 text-[10px] text-foreground/80">
                {executionProgress.steps.length} step
                {executionProgress.steps.length === 1 ? "" : "s"} ·{" "}
                {executionProgress.success ? "succeeded" : "failed"}
              </span>
            </div>
          )}
          {outputEntries.length === 0 ? (
            !executionProgress && <IoEmptyValue label="No outputs" />
          ) : (
            <>
              <div className="space-y-2.5">
                {visibleOutputEntries.map(([key, container]) => (
                  <IoEntry
                    key={key}
                    nodeId={nodeId}
                    label={resolveOutputLabel(key)}
                    container={container}
                    dataType={resolveOutputDataType(key, container)}
                    declaredIsList={resolveOutputIsList(key)}
                    iterations={
                      paged && Array.isArray(container.value)
                        ? (container.total_size ?? pageCount)
                        : undefined
                    }
                  />
                ))}
              </div>
              {hiddenOutputCount > 0 && (
                <p className="mt-2 text-center text-[10px] italic text-muted-foreground/60">
                  and {hiddenOutputCount} more{" "}
                  {hiddenOutputCount === 1 ? "output" : "outputs"}
                </p>
              )}
            </>
          )}
          {iterationOutcomes && (
            <FanOutIterationCounts
              totalIterations={totalIterations}
              outcomes={iterationOutcomes}
              className="mt-2 block text-[10px]"
            />
          )}
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setIteration(firstFailedIteration(iterationOutcomes) ?? 1);
        }}
      >
        <DialogContent
          size="full"
          className="!w-[min(90%,56rem)] !max-w-[min(90%,56rem)] h-[min(90%,40rem)] max-h-[min(90%,40rem)] flex flex-col gap-0 p-0 overflow-hidden bg-background"
        >
          <DialogHeader className="shrink-0 px-6 py-4 border-b bg-background">
            <DialogTitle>Run output</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto scrollbar-on-hover">
            {inputEntries.length > 0 && (
              <section className="px-6 py-4 border-b border-border/50">
                <p className="text-sm font-medium text-foreground mb-3">
                  Inputs
                </p>
                <div className="space-y-6">
                  {inputEntries.map(([key, container]) => (
                    <IoModalSection
                      key={key}
                      label={key}
                      container={container}
                      dataType={resolveOutputDataType(key, container)}
                    />
                  ))}
                </div>
              </section>
            )}
            {executionProgress && (
              <section className="px-6 py-4 border-b border-border/50">
                <p className="text-sm font-medium text-foreground mb-3">
                  Playbook steps
                </p>
                <PlaybookResultsViewer results={executionProgress} />
              </section>
            )}
            <section className="px-6 py-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">Outputs</p>
                {paged && (
                  <div className="flex items-center gap-2">
                    {totalIterations > pageCount && (
                      <span className="text-xs italic text-muted-foreground">
                        first {pageCount} of {totalIterations} iterations
                      </span>
                    )}
                    <FanOutIterationPager
                      page={currentPage}
                      setPage={setIteration}
                      totalPages={pageCount}
                      outcomes={iterationOutcomes}
                    />
                  </div>
                )}
              </div>
              {outcomePage && outcomePage.kind !== "ok" ? (
                <IterationOutcomeRows
                  page={outcomePage}
                  nodeId={nodeId}
                  firstFailed={firstFailedIteration(iterationOutcomes)}
                />
              ) : outputEntries.length === 0 ? (
                <IoEmptyValue label="No outputs" density="comfortable" />
              ) : (
                <div className="flex flex-col gap-2">
                  {outputEntries.map(([key, container]) => {
                    // Index within the fetched window — undefined while a
                    // later window is still loading.
                    const displayValue =
                      paged && Array.isArray(container.value)
                        ? (container.value as unknown[])[
                            ioListItemIndex(
                              currentPage,
                              container.list_offset ?? 0
                            )
                          ]
                        : container.value;
                    // Per-item error slots: only failed iterations have an
                    // error to show — successful ones carry a Skipped marker.
                    if (key === "on_error" && isSkippedValue(displayValue)) {
                      return null;
                    }
                    // Each failed iteration shows its own error page; the
                    // node-level `_error` would repeat it on succeeded pages.
                    if (container.type === "_error" && iterationOutcomes) {
                      return null;
                    }
                    return (
                      <V2OutputRow
                        key={key}
                        nodeId={nodeId}
                        label={resolveOutputLabel(key)}
                        value={displayValue}
                        type={resolveOutputDataType(key, container)}
                        isError={
                          container.type === "_error" || key === "on_error"
                        }
                        truncation={
                          container.truncated && Array.isArray(container.value)
                            ? {
                                shown: container.value.length,
                                total:
                                  container.total_size ??
                                  container.value.length,
                                file: container.preview_file ?? null,
                              }
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function IoEntry({
  nodeId,
  label,
  container,
  dataType,
  declaredIsList = false,
  iterations,
}: {
  nodeId: string;
  label: string;
  container: IoContainer;
  dataType: string;
  declaredIsList?: boolean;
  // Fan-out width when the value array holds one slot per iteration rather
  // than a real list output — labeled "<Type> · N iterations" instead of
  // "List of <Type>".
  iterations?: number;
}) {
  const raw = container.value;
  // A declared list output that produced nothing arrives as null rather than
  // `[]` — still a list, so it keeps its "List of <Type>" badge and reads
  // "Empty list" like any other empty list.
  const isList = Array.isArray(raw) || (declaredIsList && raw == null);

  if (container.type === "_error") {
    return (
      <div>
        <IoOutputLabel label={label} />
        <NodeIoValue nodeId={nodeId} container={container} />
      </div>
    );
  }

  if (isList) {
    const items = Array.isArray(raw) ? raw : [];
    return (
      <div>
        <IoOutputLabel
          label={label}
          dataType={dataType}
          isList={iterations !== undefined ? declaredIsList : true}
          iterations={iterations}
        />
        <CompactListPreview
          items={items}
          dataType={dataType}
          className="nodrag"
          maxVisible={2}
          stopClickPropagation={false}
          totalItems={container.truncated ? container.total_size : undefined}
        />
      </div>
    );
  }

  return (
    <div>
      <IoOutputLabel label={label} />
      <NodeIoValue nodeId={nodeId} container={container} />
    </div>
  );
}

function getSingleCopyableText(container: IoContainer): string | null {
  if (container.type === "_error" || Array.isArray(container.value)) {
    return null;
  }

  const textContainer = coerceIoTextContainer(container.value);
  if (textContainer) return textContainer.text;

  if (
    typeof container.value === "string" &&
    !coerceIoFileLike(container.value, {
      parseStrings: isIoFileType(container.type),
    })
  ) {
    return container.value;
  }

  return null;
}

function CopyTextButton({ text }: { text: string }) {
  return (
    <NoxusTooltip content="Copy" zIndex={1400} asChild>
      <Button
        aria-label="Copy"
        variant="ghost"
        size="iconsm"
        className="shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(text);
        }}
      >
        <Copy strokeWidth={1.33} className="text-muted-foreground" />
      </Button>
    </NoxusTooltip>
  );
}

function IoModalSection({
  label,
  container,
  dataType,
}: {
  label: string;
  container: IoContainer;
  dataType: string;
}) {
  const raw = container.value;
  const isList = Array.isArray(raw) && container.type !== "_error";
  const copyText = getSingleCopyableText(container);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <IoOutputLabel
          label={label}
          dataType={isList ? dataType : undefined}
          isList={isList}
          size="md"
        />
        {copyText !== null && <CopyTextButton text={copyText} />}
      </div>
      <IoFullView container={container} />
    </div>
  );
}

function IoOutputLabel({
  label,
  dataType,
  isList = false,
  iterations,
  size = "sm",
}: {
  label: string;
  dataType?: string;
  isList?: boolean;
  iterations?: number;
  size?: "sm" | "md";
}) {
  const isMd = size === "md";
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5",
        isMd ? "mb-0" : "mb-1"
      )}
    >
      <span
        className={cn(
          "font-medium text-muted-foreground",
          isMd
            ? "text-sm text-foreground"
            : "text-[9px] text-muted-foreground/60"
        )}
      >
        {label}
      </span>
      {dataType !== undefined && (isList || iterations !== undefined) && (
        <>
          <span className="text-muted-foreground/30">·</span>
          <DataTypeBadge
            dataType={dataType}
            isList={isList}
            className={
              isMd
                ? "text-xs [&_svg]:size-3"
                : "text-[9px] text-muted-foreground/60 [&_svg]:size-[9px]"
            }
          />
        </>
      )}
      {iterations !== undefined && (
        <>
          <span className="text-muted-foreground/30">·</span>
          <span
            className={cn(
              "text-muted-foreground/60",
              isMd ? "text-xs" : "text-[9px]"
            )}
          >
            {iterations} iterations
          </span>
        </>
      )}
    </div>
  );
}

function listKindToDataType(kind: string): string {
  switch (kind) {
    case "Files":
      return "File";
    case "Text":
      return "str";
    case "Numbers":
      return "number";
    case "Booleans":
      return "bool";
    case "Objects":
      return "object";
    default:
      return "object";
  }
}

function inferListKind(items: unknown[], containerType?: string): string {
  const normalizedType = normalizeIoContainerType(containerType, items[0]);
  if (normalizedType === "File") return "Files";
  if (items.length === 0) return "Items";

  let files = 0;
  let text = 0;
  let numbers = 0;
  let booleans = 0;
  let objects = 0;

  // Skipped iteration slots serialize to `{ skipped: true }`; excluded from
  // the kind tally so an all-skipped output isn't mislabeled "List of Object".
  let counted = 0;
  for (const item of items) {
    if (isSkippedValue(item)) continue;
    counted++;
    if (coerceIoFileLike(item)) files++;
    else if (typeof item === "string") text++;
    else if (typeof item === "number") numbers++;
    else if (typeof item === "boolean") booleans++;
    else objects++;
  }

  if (counted === 0) return "Items";
  const n = counted;
  if (files === n) return "Files";
  if (text === n) return "Text";
  if (numbers === n) return "Numbers";
  if (booleans === n) return "Booleans";
  if (objects === n) return "Objects";
  if (files > 0 && files >= text) return "Files";
  return "Items";
}

function NodeIoValue({
  nodeId,
  container,
}: {
  nodeId: string;
  container: IoContainer;
}) {
  if (container.type === "_error") {
    const record =
      typeof container.value === "string"
        ? tryParseJsonObject(container.value)
        : container.value;
    // Errors that ship structured refs (iteration width mismatch) resolve to
    // the same source/output chips the editor shows, instead of raw
    // `<uuid>.output` a flow author can't read.
    const widths =
      record && typeof record === "object" && !Array.isArray(record)
        ? iterationWidths(record as Record<string, unknown>)
        : null;
    // Same field priority as the run drawer's ErrorDetailsView, so both
    // surfaces show the same text.
    const err =
      record && typeof record === "object" && !Array.isArray(record)
        ? (record as { markdown?: string; message?: string })
        : null;
    return (
      <div className="rounded-md border border-red-200/60 bg-red-50/50 px-2 py-1.5 text-[10px] text-red-600 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400">
        {widths ? (
          <IterationWidthErrorView nodeId={nodeId} widths={widths} compact />
        ) : (
          <SpotMarkdown
            markdown={err?.markdown ?? err?.message ?? "Error"}
            options={{
              inline: true,
              breaks: true,
              markdownClass: "line-clamp-4",
            }}
          />
        )}
      </div>
    );
  }

  return <IoValueBody container={container} compact />;
}

export function IoValueBody({
  container,
  compact,
}: {
  container: IoContainer;
  compact: boolean;
}) {
  // Strings only render as files when the container's DECLARED type is
  // File/Image — a str output whose text looks like a file stays text.
  const parseStrings = isIoFileType(container.type);
  const file = coerceIoFileLike(container.value, { parseStrings });
  if (file) {
    return <IoFileRow value={file} compact={compact} />;
  }

  const textContainer = coerceIoTextContainer(container.value);
  if (textContainer) {
    if (
      textContainer.text.trim() === "" &&
      !textContainer.has_preview &&
      !textContainer.file
    ) {
      return <EmptyValue compact={compact} />;
    }
    return <IoTextContainerValue value={textContainer} compact={compact} />;
  }

  const raw = container.value;

  if (isSkippedValue(raw)) {
    return <SkippedBadge compact={compact} />;
  }

  if (raw === null || raw === undefined) {
    return <EmptyValue compact={compact} />;
  }

  if (Array.isArray(raw)) {
    return <ListPreview items={raw} containerType={container.type} />;
  }

  if (typeof raw === "number" || typeof raw === "boolean") {
    return compact ? (
      <span className="inline-block rounded-md border border-border/50 bg-muted/20 px-1.5 py-0.5 text-[10px] font-mono text-foreground/80">
        {String(raw)}
      </span>
    ) : (
      <ValueSurface>
        <span className="text-sm font-mono text-foreground">{String(raw)}</span>
      </ValueSurface>
    );
  }

  if (typeof raw === "string") {
    if (raw.trim() === "") {
      return <EmptyValue compact={compact} />;
    }
    const stringFile = coerceIoFileLike(raw, { parseStrings });
    if (stringFile) {
      return <IoFileRow value={stringFile} compact={compact} />;
    }

    const jsonValue = tryParseJsonObject(raw);

    // Error-shaped payloads (a node's on_error) read better as a message + a
    // newline-preserving traceback than as escaped JSON.
    const errorShaped =
      jsonValue !== null &&
      !Array.isArray(jsonValue) &&
      typeof (jsonValue as { message?: unknown }).message === "string";
    if (errorShaped && !compact) {
      const err = jsonValue as { message: string; snippet?: string };
      return (
        <ValueSurface>
          <div className="space-y-2">
            <p className="text-sm text-foreground whitespace-pre-wrap break-words">
              {err.message}
            </p>
            {err.snippet && err.snippet !== err.message && (
              <pre className="text-xs text-foreground/80 whitespace-pre-wrap break-words font-mono leading-relaxed rounded-md border border-border/40 bg-muted/20 p-3">
                {err.snippet}
              </pre>
            )}
          </div>
        </ValueSurface>
      );
    }

    const displayText =
      jsonValue !== null ? JSON.stringify(jsonValue, null, 2) : raw;
    const isJson = jsonValue !== null;

    return compact ? (
      <div className="rounded-md border border-border/40 bg-muted/15 px-2 py-1.5 text-[10px] text-foreground/80">
        {isJson ? (
          <p className="line-clamp-4 break-words whitespace-pre-wrap font-mono">
            {displayText}
          </p>
        ) : (
          <SpotMarkdown
            markdown={raw}
            options={{
              inline: true,
              breaks: true,
              markdownClass: "line-clamp-4",
            }}
          />
        )}
      </div>
    ) : isJson ? (
      <ValueSurface>
        <pre className="text-xs text-foreground/90 whitespace-pre-wrap break-words font-mono leading-relaxed">
          {displayText}
        </pre>
      </ValueSurface>
    ) : (
      <ValueSurface>
        <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed">
          {raw}
        </p>
      </ValueSurface>
    );
  }

  return compact ? (
    <div className="rounded-md border border-border/40 bg-muted/15 px-2 py-1.5">
      <pre className="line-clamp-4 break-words whitespace-pre-wrap font-mono text-[9px] text-foreground/70">
        {JSON.stringify(raw, null, 2)}
      </pre>
    </div>
  ) : (
    <ValueSurface>
      <pre className="text-xs text-foreground/90 whitespace-pre-wrap break-words font-mono leading-relaxed">
        {JSON.stringify(raw, null, 2)}
      </pre>
    </ValueSurface>
  );
}

function EmptyValue({ compact }: { compact: boolean }) {
  return <IoEmptyValue density={compact ? "compact" : "comfortable"} />;
}

function ValueSurface({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-border/50 bg-card px-3 py-2.5">
      {children}
    </div>
  );
}

function IoTextContainerValue({
  value,
  compact,
}: {
  value: IoTextContainer;
  compact: boolean;
}) {
  const href = value.file ? getIoFileHref(value.file, false) : "";

  if (compact) {
    return (
      <div className="rounded-md border border-border/40 bg-muted/15 px-2 py-1.5 text-[10px] text-foreground/80">
        <SpotMarkdown
          markdown={value.text}
          options={{
            inline: true,
            breaks: true,
            markdownClass: "line-clamp-4",
          }}
        />
        {value.has_preview && (
          <div className="mt-1 flex items-center gap-1 text-[9px] text-muted-foreground/60">
            <span className="italic">Truncated due to output size</span>
            {href && (
              <Link
                href={href}
                download
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-0.5 not-italic text-primary hover:underline"
              >
                <Download className="size-2.5" />
                Download
              </Link>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <ValueSurface>
      <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed">
        {value.text}
        {value.has_preview && (
          <span className="italic text-muted-foreground">
            {" "}
            … (truncated due to output size)
          </span>
        )}
      </p>
      {value.has_preview && href && (
        <Link
          href={href}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Download className="size-3.5" />
          Download full output
        </Link>
      )}
    </ValueSurface>
  );
}

function IoFullView({ container }: { container: IoContainer }) {
  if (container.type === "_error") {
    const err = container.value as {
      message?: string;
      snippet?: string;
    } | null;
    return (
      <div className="rounded-md border border-red-200/60 bg-red-50/40 p-4 space-y-2 dark:border-red-900/40 dark:bg-red-950/15">
        {err?.message && (
          <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
            {err.message}
          </p>
        )}
        {err?.snippet && err.snippet !== err.message && (
          <pre className="text-xs text-red-600/90 dark:text-red-400/90 whitespace-pre-wrap break-words rounded-md border border-red-200/40 dark:border-red-900/30 bg-red-50/60 dark:bg-red-950/25 p-3 font-mono">
            {err.snippet}
          </pre>
        )}
      </div>
    );
  }

  return <IoValueBody container={container} compact={false} />;
}

function ListPreview({
  items,
  containerType,
}: {
  items: unknown[];
  containerType?: string;
}) {
  const [query, setQuery] = useState("");

  const stringified = useMemo(
    () => items.map((item) => listItemSearchText(item)),
    [items]
  );

  const filteredIndices = useMemo(() => {
    if (!query.trim()) return items.map((_, i) => i);
    const q = query.toLowerCase();
    const out: number[] = [];
    for (let i = 0; i < stringified.length; i++) {
      if (stringified[i].toLowerCase().includes(q)) out.push(i);
    }
    return out;
  }, [items, stringified, query]);

  const listKind = inferListKind(items, containerType);
  const showSearch = items.length > 8;

  if (items.length === 0) {
    return <IoEmptyValue label="Empty list" density="comfortable" />;
  }

  return (
    <div className="space-y-2 nodrag" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground tabular-nums">
          {filteredIndices.length === items.length
            ? `${items.length} ${items.length === 1 ? "item" : "items"}`
            : `${filteredIndices.length} of ${items.length} items`}
          <span className="text-muted-foreground/50"> · {listKind}</span>
        </p>
        {showSearch && (
          <div className="relative w-44 shrink-0">
            <Search
              size={12}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/45 pointer-events-none"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter…"
              className="w-full h-7 text-xs pl-7 pr-2 rounded-md bg-background border border-border/60 focus:outline-none focus:ring-1 focus:ring-ring/30"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
      <div className="rounded-md border border-border/60 overflow-hidden bg-card">
        <div className="max-h-[min(50vh,20rem)] overflow-y-auto scrollbar-on-hover divide-y divide-border/50">
          {filteredIndices.length === 0 ? (
            <p className="text-xs text-muted-foreground italic px-3 py-3">
              No matches
            </p>
          ) : (
            filteredIndices.map((idx) => (
              <ListRow key={idx} index={idx} item={items[idx]} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function listItemSearchText(item: unknown): string {
  const file = coerceIoFileLike(item);
  if (file) return file.name;
  if (typeof item === "string") return item;
  if (typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }
  if (item === null) return "null";
  try {
    return JSON.stringify(item);
  } catch {
    return "";
  }
}

function ListRow({ index, item }: { index: number; item: unknown }) {
  const file = coerceIoFileLike(item);
  const stringCopyText =
    typeof item === "string" && !coerceIoFileLike(item) ? item : null;

  return (
    <div className="flex items-start gap-3 px-3 py-2 hover:bg-muted/30 transition-colors">
      <span className="text-[10px] font-mono text-muted-foreground/50 tabular-nums shrink-0 pt-0.5 w-6 text-right">
        {index}
      </span>
      <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
        {file ? <IoFileRow value={file} /> : <ListRowValue item={item} />}
      </div>
      {stringCopyText !== null && <CopyTextButton text={stringCopyText} />}
    </div>
  );
}

function ListRowValue({ item }: { item: unknown }) {
  if (typeof item === "string") {
    const stringFile = coerceIoFileLike(item);
    if (stringFile) {
      return <IoFileRow value={stringFile} />;
    }
    return (
      <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-snug">
        {item}
      </p>
    );
  }

  if (typeof item === "number" || typeof item === "boolean") {
    return (
      <span className="text-sm font-mono text-foreground">{String(item)}</span>
    );
  }

  if (item === null || item === undefined) {
    return <span className="text-sm italic text-muted-foreground">null</span>;
  }

  if (isSkippedValue(item)) {
    return <SkippedBadge />;
  }

  return (
    <div className="min-w-0 max-w-full overflow-x-auto">
      <NoxusJsonView data={item as object} expand={2} />
    </div>
  );
}
